import { mkdir, writeFile } from 'node:fs/promises';
import path from 'node:path';
import type { PrismaClient } from '@prisma/client';
import nodemailer, { type SendMailOptions } from 'nodemailer';
import type { Config } from '../../core/config.js';
import { HttpError } from '../../core/errors.js';
import { enqueueJob, registerJobHandler } from '../../core/jobs.js';

/** Variables a message template may use. Anything else renders empty (never evaluated). */
export const TEMPLATE_VARIABLES = [
  'client_name',
  'document_number',
  'total',
  'due_date',
  'valid_until',
  'link',
  'company_name',
  'sender_name',
  'reset_link',
  'invite_link',
  'user_name',
] as const;

export function renderTemplate(
  body: string,
  vars: Partial<Record<(typeof TEMPLATE_VARIABLES)[number], string>>,
) {
  const allowed = new Set<string>(TEMPLATE_VARIABLES);
  return body.replace(/\{\{\s*([a-z_]+)\s*\}\}/g, (_m, name: string) =>
    allowed.has(name) ? (vars[name as (typeof TEMPLATE_VARIABLES)[number]] ?? '') : '',
  );
}

const EMAIL_RE = /^[^\s@<>()[\]\\,;:"]+@[^\s@<>()[\]\\,;:"]+\.[^\s@<>()[\]\\,;:"]{2,}$/;

/** Rejects malformed addresses and anything that could inject extra headers (CR/LF). */
export function assertValidEmail(address: string): string {
  const trimmed = address.trim();
  if (/[\r\n]/.test(address) || trimmed.length > 254 || !EMAIL_RE.test(trimmed)) {
    throw new HttpError(400, 'INVALID_RECIPIENT', `Invalid email address: ${address.slice(0, 60)}`);
  }
  return trimmed;
}

export function assertSafeHeader(value: string, field: string): string {
  if (/[\r\n]/.test(value))
    throw new HttpError(400, 'INVALID_HEADER', `${field} must not contain line breaks`);
  return value.trim();
}

export interface SendEmailInput {
  companyId: number;
  to: string[];
  subject: string;
  text: string;
  docType?: string;
  docId?: number;
  templateName?: string;
  /** Attach the PDF of this document (generated when the message is actually sent). */
  attachment?: { type: string; id: number };
  sentById?: number;
}

/** Queues one communication + one job per recipient. Nothing is sent inline. */
export async function queueEmail(db: PrismaClient, input: SendEmailInput) {
  if (input.to.length === 0)
    throw new HttpError(400, 'NO_RECIPIENTS', 'At least one recipient is required');
  if (input.to.length > 20)
    throw new HttpError(400, 'TOO_MANY_RECIPIENTS', 'At most 20 recipients per send');
  const subject = assertSafeHeader(input.subject, 'Subject');
  const recipients = [...new Set(input.to.map(assertValidEmail))];

  const created = [];
  for (const to of recipients) {
    const comm = await db.communication.create({
      data: {
        companyId: input.companyId,
        docType: input.docType,
        docId: input.docId,
        channel: 'email',
        recipient: to,
        subject,
        bodyText: input.text,
        templateName: input.templateName,
        attachmentType: input.attachment?.type,
        attachmentId: input.attachment?.id,
        sentById: input.sentById,
        status: 'queued',
      },
    });
    await enqueueJob(db, {
      companyId: input.companyId,
      type: 'email.send',
      payload: { communicationId: comm.id },
      idempotencyKey: `comm:${comm.id}`,
    });
    created.push(comm);
  }
  return created;
}

type Transport = { sendMail: (opts: SendMailOptions) => Promise<unknown> };

export function createTransport(config: Config): Transport {
  if (config.SMTP_HOST) {
    return nodemailer.createTransport({
      host: config.SMTP_HOST,
      port: config.SMTP_PORT,
      secure: config.SMTP_SECURE,
      auth: config.SMTP_USER ? { user: config.SMTP_USER, pass: config.SMTP_PASS } : undefined,
    });
  }
  // Development/test: write the message to disk instead of sending it.
  return {
    async sendMail(opts) {
      const dir = path.resolve(process.cwd(), 'storage', 'outbox');
      await mkdir(dir, { recursive: true });
      const file = path.join(dir, `${Date.now()}-${Math.random().toString(36).slice(2, 8)}.json`);
      // Attachments are summarised so the dev outbox stays readable.
      const summary = {
        ...opts,
        attachments: (opts.attachments ?? []).map((a) => ({
          filename: a.filename,
          bytes: Buffer.isBuffer(a.content) ? a.content.length : undefined,
        })),
      };
      await writeFile(file, JSON.stringify(summary, null, 2), 'utf8');
    },
  };
}

let transportOverride: Transport | undefined;
/** Tests inject a fake transport here. */
export const setTransportForTests = (t: Transport | undefined) => {
  transportOverride = t;
};

/** Registers the `email.send` job handler. Called once at start-up. */
export function registerMessagingJobs() {
  registerJobHandler('email.send', async (job, ctx) => {
    const { communicationId } = job.payload as { communicationId: number };
    const comm = await ctx.db.communication.findUnique({ where: { id: communicationId } });
    if (!comm) throw new Error(`Communication ${communicationId} not found`);
    if (comm.status === 'sent') return; // idempotent: already delivered

    try {
      let attachments: SendMailOptions['attachments'];
      if (comm.attachmentType && comm.attachmentId) {
        // Generated now, so the PDF always reflects the document as it is when the message goes out.
        const { renderDocumentPdf } = await import('../sales/pdf.service.js');
        const { pdf, filename } = await renderDocumentPdf(
          ctx.db,
          comm.companyId,
          comm.attachmentType as never,
          comm.attachmentId,
        );
        attachments = [{ filename, content: pdf, contentType: 'application/pdf' }];
      }
      await (transportOverride ?? createTransport(ctx.config)).sendMail({
        from: ctx.config.MAIL_FROM,
        to: comm.recipient,
        subject: comm.subject ?? '',
        text: comm.bodyText ?? '',
        attachments,
      });
      await ctx.db.communication.update({
        where: { id: comm.id },
        data: { status: 'sent', sentAt: new Date(), error: null },
      });
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      // Final attempt marks the communication failed; earlier attempts leave it queued for retry.
      await ctx.db.communication.update({
        where: { id: comm.id },
        data: {
          status: job.attempts >= job.maxAttempts ? 'failed' : 'queued',
          error: message.slice(0, 1000),
        },
      });
      throw err;
    }
  });
}
